import z from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { showNotification } from '@mantine/notifications'
import { DocumentReference } from '@medplum/fhirtypes'
import { WithId } from '@medplum/core'
import { useMiddlewareClient } from './useMiddlewareClient'
import { useClient } from './useClient'
import { faxDetailQueryKey, faxInboxQueryKey } from './useFaxInbox'
import { faxPatientsQueryKey } from './useFaxPatients'

export const FAX_DOCUMENT_TYPES = ['lab', 'document'] as const
export type FaxDocumentType = (typeof FAX_DOCUMENT_TYPES)[number]

export const faxTriageSchema = z
  .object({
    documentType: z.enum(FAX_DOCUMENT_TYPES, { message: 'Document type is required' }),
    title: z.string().min(1, { message: 'Title is required' }),
    assignedPatient: z.object(
      {
        reference: z.string(),
      },
      { error: 'Patient is required' },
    ),
    // Optional, and the picker stores null once cleared — including while it's hidden
    // for a Document, where an invalid value would block the submit with no message.
    referringProvider: z
      .object({
        reference: z.string(),
      })
      .nullish(),
    labName: z.string(),
    notifyPatient: z.boolean(),
  })
  // Lab is a Labs/Imaging field only — a Document has no lab (PD-1869).
  .refine((data) => data.documentType !== 'lab' || !!data.labName, {
    path: ['labName'],
    message: 'Lab is required',
    // A refinement is skipped by default while any other field is invalid, which
    // would hide this error until the patient and title were fixed. Run it whenever
    // the two fields it reads parsed, so every missing field is named at once.
    when: (payload) =>
      !payload.issues.some((i) => i.path?.[0] === 'documentType' || i.path?.[0] === 'labName'),
  })

export type FaxTriageParams = z.input<typeof faxTriageSchema>

export function useFaxTriage(documentReference: WithId<DocumentReference>, taskId: string) {
  const middlewareClient = useMiddlewareClient()
  const client = useClient()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: FaxTriageParams) => {
      const patientId = data.assignedPatient?.reference.split('/').pop()
      if (!patientId) throw new Error('Patient is required')

      if (data.documentType === 'document') {
        return client.triageIncomingFaxDocument({ id: taskId, patientId, title: data.title })
      }

      const referringProviderId = data.referringProvider?.reference.split('/').pop()

      return middlewareClient.post('/order/fax-triage/lab', {
        documentReferenceId: documentReference.id,
        patientId,
        title: data.title,
        ...(referringProviderId ? { referringProviderId } : {}),
        ...(data.labName ? { labName: data.labName } : {}),
        notifyPatient: data.notifyPatient,
      })
    },
    onSuccess: async (_result, data) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: faxInboxQueryKey({}) }),
        queryClient.invalidateQueries({ queryKey: faxDetailQueryKey(taskId) }),
      ])

      showNotification({
        color: 'green',
        title: 'Fax triaged',
        message:
          data.documentType === 'document'
            ? "Fax filed to the patient's documents."
            : 'Lab report created successfully.',
      })
    },
    onError: async (error, data) => {
      showNotification({
        color: 'red',
        title: 'Error',
        message:
          serverMessage(error) ??
          (data.documentType === 'document'
            ? 'Failed to triage fax as a document.'
            : 'Failed to triage fax as lab report.'),
      })
      // A refusal because a split filing claimed the fax (PD-1988): the refetch brings up the
      // split, which always shows once it is in flight.
      await queryClient.invalidateQueries({ queryKey: faxPatientsQueryKey(taskId) })
    },
  })
}

// The middleware's own explanation of a refusal, e.g. "This fax is being split — finish the split
// instead". The oRPC client throws an Error carrying it; the REST client throws Nest's body.
function serverMessage(error: unknown): string | undefined {
  const message = (error as { message?: unknown } | null)?.message
  return typeof message === 'string' && message ? message : undefined
}
